/**
 * QualifyModal (2026-06-17) — link an UNMATCHED inbox conversation to a customer
 * record ("qualify" it, in Pegasus terms). Born from Denise Haugen showing "no
 * customer match" though her phone was on file: the review link was an outbound-
 * logged message the auto-matcher never ran against.
 *
 * Leads with a high-confidence SUGGESTED match (the phone-on-file matcher), then
 * a name search for everything else. Confirming calls pegasus_qualify_conversation,
 * which records the link durably AND back-stamps the conversation's messages, so
 * the thread immediately attaches to the customer (360 link, history, etc.).
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Search, UserCheck, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { InboxConversation } from "./api";

const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);
const isPhone = (s: string | null): s is string => !!s && !s.includes("@") && p10(s).length === 10;
const fmtName = (full: string | null) => {
  if (!full) return "Unknown";
  const f = full.replace(/\s+\d{4,}$/, "");
  const m = f.match(/^([^,]+),(.+)$/);
  return (m ? `${m[2].trim()} ${m[1].trim()}` : f).trim();
};

interface Cand {
  custno: string;
  fullname: string | null;
  city: string | null;
  state: string | null;
  phones: string;
}
type Row = { custno: string; fullname: string | null; city: string | null; state: string | null; phone1: string | null; phone2: string | null; phone3: string | null; phone4: string | null };
const toCand = (c: Row): Cand => ({
  custno: c.custno,
  fullname: c.fullname,
  city: c.city,
  state: c.state,
  phones: [c.phone1, c.phone2, c.phone3, c.phone4].filter(Boolean).join(", "),
});

export function QualifyModal({
  conversation,
  myEmail,
  onClose,
  onQualified,
}: {
  conversation: InboxConversation;
  myEmail: string | null;
  onClose: () => void;
  onQualified: (custno: string) => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { data: suggested } = useQuery({
    queryKey: ["qualify_suggest", conversation.endpoint],
    enabled: isPhone(conversation.endpoint),
    queryFn: async (): Promise<Cand | null> => {
      const { data: m } = await supabase.rpc("pegasus_match_phones", { p10s: [p10(conversation.endpoint!)] });
      const custno = (m as { custno: string }[] | null)?.[0]?.custno;
      if (!custno) return null;
      const { data: c } = await supabase
        .from("mirror_customer")
        .select("custno, fullname, city, state, phone1, phone2, phone3, phone4")
        .eq("custno", custno)
        .maybeSingle();
      return c ? toCand(c as Row) : null;
    },
  });

  const { data: results, isFetching } = useQuery({
    queryKey: ["qualify_search", q],
    enabled: q.trim().length >= 2,
    queryFn: async (): Promise<Cand[]> => {
      // fullname is "Last,First …" — AND each typed word so "Haugen Denise"
      // (or "Denise Haugen") matches "Haugen,Denise" regardless of order.
      let query = supabase
        .from("mirror_customer")
        .select("custno, fullname, city, state, phone1, phone2, phone3, phone4");
      for (const tok of q.trim().split(/\s+/).filter(Boolean)) {
        query = query.ilike("fullname", `%${tok}%`);
      }
      const { data } = await query.limit(12);
      return ((data ?? []) as Row[]).map(toCand);
    },
  });

  const qualify = async (custno: string) => {
    setBusy(custno);
    setErr(null);
    const { error } = await supabase.rpc("pegasus_qualify_conversation", {
      p_endpoint: conversation.endpoint,
      p_custno: custno,
      p_by: myEmail,
    });
    if (error) {
      setErr(error.message);
      setBusy(null);
      return;
    }
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["inbox_conversations"] }),
      qc.invalidateQueries({ queryKey: ["conversation_messages"] }),
    ]);
    onQualified(custno);
    onClose();
  };

  const CandRow = ({ c, hint }: { c: Cand; hint?: string }) => (
    <button
      type="button"
      onClick={() => qualify(c.custno)}
      disabled={!!busy}
      className="flex w-full items-center gap-3 rounded-lg border border-hairline bg-white px-3 py-2 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40 disabled:opacity-50"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">{fmtName(c.fullname)}</div>
        <div className="truncate text-[11px] text-slate-500">
          #{c.custno}
          {c.city ? ` · ${c.city}, ${c.state ?? ""}` : ""}
          {c.phones ? ` · ${c.phones}` : ""}
        </div>
      </div>
      {busy === c.custno ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-600" />
      ) : hint ? (
        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{hint}</span>
      ) : (
        <UserCheck className="h-4 w-4 shrink-0 text-slate-300" />
      )}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-24" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md overflow-hidden rounded-xl border border-hairline bg-white shadow-e3">
        <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">Qualify Conversation</h3>
            <p className="truncate text-[11px] text-slate-500">
              Link {conversation.provider_contact_name || conversation.endpoint} to a customer record
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-4">
          {suggested && (
            <div>
              <div className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                <Sparkles className="h-3 w-3" /> Likely match — phone on file
              </div>
              <CandRow c={suggested} hint="phone match" />
            </div>
          )}
          <div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customers by name…"
                className="w-full rounded-lg border border-hairline bg-white py-2 pl-8 pr-3 text-sm focus:border-brand-400 focus:outline-none"
              />
            </div>
            <div className="mt-2 space-y-1.5">
              {isFetching && <div className="px-1 py-2 text-xs text-slate-400">Searching…</div>}
              {!isFetching && q.trim().length >= 2 && (results ?? []).length === 0 && (
                <div className="px-1 py-2 text-xs text-slate-400">No customers match “{q}”.</div>
              )}
              {(results ?? []).map((c) => (
                <CandRow key={c.custno} c={c} />
              ))}
            </div>
          </div>
          {err && <p className="rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">{err}</p>}
        </div>
      </div>
    </div>
  );
}
